export interface ArContato {
  nome: string;
  funcao: string;
  contato: string;
}

export interface ArDetalhesMaquina {
  certificacaoCe: string;
  dataColocacaoMercado: string;
  dataInspecaoInLoco: string;
  descricao: string;
}

export interface ArLimitesMaquina {
  modosMaquina: string[];
  usoMaquina: string;
  numeroOperadores: string;
  treinamentoUsuarios: string;
  tamanhoMaquina: string;
  interfaceFonteAlimentacao: string;
  vidaUtilMaquina: string;
  intervaloServicoRecomendado: string;
}

export interface ArCamposDocumento {
  versaoTemplate: string;
  nomeRelatorio: string;
  cliente: string;
  idDocumento: string;
  descricaoRelatorio: string;
  criadoPor: ArContato;
  aprovadoPor: ArContato;
  contatosCliente: ArContato[];
  contatosTecnicum: ArContato[];
  normasNacionais: string[];
  normasInternacionais: string[];
  detalhesMaquina: ArDetalhesMaquina;
  limitesMaquina: ArLimitesMaquina;
}

export interface ArCamposClienteWrapper {
  ar: ArCamposDocumento;
}

export const AR_TEMPLATE_VERSION = 'ar-blueprint-2026-02';

export const AR_NORMAS_NACIONAIS_PADRAO = [
  'ABNT NBR ISO 12100:2013 - Segurança de máquinas - Princípios gerais de projeto - Apreciação e redução de riscos',
  'ABNT ISO/TR 14121-2:2018 - Segurança de máquinas - Apreciação de riscos - Parte 2: Guia prático e exemplos de métodos',
  'ABNT NBR IEC 60204-1:2020 - Segurança de máquinas - Equipamentos Elétricos de Máquinas - Parte 1: Requisitos Gerais',
  'ABNT NBR ISO 13849-1:2019 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança - Parte 1',
  'ABNT NBR ISO 13849-2:2019 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança - Parte 2',
  'ABNT NBR ISO 13850:2021 - Segurança de máquinas - Função de Parada de Emergência',
  'ABNT NBR ISO 13857:2021 - Segurança de máquinas - Distâncias de segurança para impedir o acesso a zonas de perigo',
  'ABNT NBR ISO 14118:2022 - Segurança de máquinas - Prevenção de partida inesperada',
  'ABNT NBR ISO 14119:2021 - Segurança de máquinas - Dispositivos de Intertravamento Associados às Proteções',
  'ABNT NBR ISO 14120:2023 - Segurança de máquinas - Proteções - Requisitos gerais para projeto, construção e seleção',
];

export const AR_NORMAS_INTERNACIONAIS_PADRAO = [
  'ISO 19353:2019 - Safety of machinery - Fire prevention and fire protection',
  'ISO 20607:2019 - Safety of machinery - Instruction handbook - General drafting principles',
  'ISO 7010:2019 - Graphical symbols - Safety colors and safety signs',
  'IEC 61496-1:2020 - Safety of machinery - Electro-sensitive protective equipment - Part 1',
  'IEC 62046:2018 - Safety of machinery - Application of protective equipment to detect the presence of persons',
  'IEC 61800-5-2 - Adjustable speed electrical power drive systems - Functional safety requirements',
];

export function criarArCamposDocumentoDefault(cliente = ''): ArCamposDocumento {
  return {
    versaoTemplate: AR_TEMPLATE_VERSION,
    nomeRelatorio: '',
    cliente,
    idDocumento: '',
    descricaoRelatorio: '',
    criadoPor: { nome: '', funcao: '', contato: '' },
    aprovadoPor: { nome: '', funcao: '', contato: '' },
    contatosCliente: [],
    contatosTecnicum: [],
    normasNacionais: [],
    normasInternacionais: [],
    detalhesMaquina: {
      certificacaoCe: '',
      dataColocacaoMercado: '',
      dataInspecaoInLoco: '',
      descricao: '',
    },
    limitesMaquina: {
      modosMaquina: ['Normal', 'Limpeza', 'Manutenção'],
      usoMaquina: '',
      numeroOperadores: '',
      treinamentoUsuarios: '',
      tamanhoMaquina: '',
      interfaceFonteAlimentacao: '',
      vidaUtilMaquina: '',
      intervaloServicoRecomendado: '',
    },
  };
}

function normalizarContato(valor: any): ArContato {
  return {
    nome: valor?.nome || '',
    funcao: valor?.funcao || valor?.cargo || '',
    contato: valor?.contato || valor?.dadosContato || '',
  };
}

export function normalizarArCamposDocumento(valor: unknown, cliente = ''): ArCamposDocumento {
  const base = criarArCamposDocumentoDefault(cliente);
  const origem = (valor && typeof valor === 'object' ? valor : {}) as Partial<ArCamposDocumento>;
  const detalhes = (origem.detalhesMaquina || {}) as Partial<ArDetalhesMaquina>;
  const limites = (origem.limitesMaquina || {}) as Partial<ArLimitesMaquina>;

  return {
    versaoTemplate: origem.versaoTemplate || base.versaoTemplate,
    nomeRelatorio: origem.nomeRelatorio || '',
    cliente: origem.cliente || cliente,
    idDocumento: origem.idDocumento || '',
    descricaoRelatorio: origem.descricaoRelatorio || '',
    criadoPor: normalizarContato(origem.criadoPor),
    aprovadoPor: normalizarContato(origem.aprovadoPor),
    contatosCliente: Array.isArray(origem.contatosCliente) ? origem.contatosCliente.map(normalizarContato) : [],
    contatosTecnicum: Array.isArray(origem.contatosTecnicum) ? origem.contatosTecnicum.map(normalizarContato) : [],
    normasNacionais: Array.isArray(origem.normasNacionais) ? origem.normasNacionais : [],
    normasInternacionais: Array.isArray(origem.normasInternacionais) ? origem.normasInternacionais : [],
    detalhesMaquina: {
      certificacaoCe: detalhes.certificacaoCe || '',
      dataColocacaoMercado: detalhes.dataColocacaoMercado || '',
      dataInspecaoInLoco: detalhes.dataInspecaoInLoco || '',
      descricao: detalhes.descricao || '',
    },
    limitesMaquina: {
      modosMaquina: Array.isArray(limites.modosMaquina) && limites.modosMaquina.length > 0
        ? limites.modosMaquina
        : base.limitesMaquina.modosMaquina,
      usoMaquina: limites.usoMaquina || '',
      numeroOperadores: limites.numeroOperadores || '',
      treinamentoUsuarios: limites.treinamentoUsuarios || '',
      tamanhoMaquina: limites.tamanhoMaquina || '',
      interfaceFonteAlimentacao: limites.interfaceFonteAlimentacao || '',
      vidaUtilMaquina: limites.vidaUtilMaquina || '',
      intervaloServicoRecomendado: limites.intervaloServicoRecomendado || '',
    },
  };
}
